import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Link,
  useSearchParams,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Fab, Box, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';

import OverLayLoader from '../../../components/OverLayLoader';
import DictionaryFilter from '../../../components/DictionaryFilter';
import FunctionalTable from '../../../components/FunctionalTable';
import NowrapWrapper from '../../../components/NowrapWrapper';
import { EditableCell } from '../../../components/EditableCell';
import { SelectColumnFilter } from '../../../components/ColumnFilter';
import FloatingActionButtonHolder from '../../../components/FloatingActionButtonHolder';

import {
  getRelatedCode,
  getLanguages,
  removeLink,
  updateLink,
  downloadRelatedCode,
} from '../../../services';
import useTitle from '../../../hooks/useTitle';
import usePersistColumns from '../../../hooks/usePersistColumns';
import useDeleteRow from '../../../hooks/useDeleteRow';
import DeleteConfirmDialog from '../../../components/DeleteConfirmDialog';
import DeleteRowButton from '../../../components/DeleteRowButton';
import {
  languageOption,
  optionalRelatedCodeColumns,
  RELATIONS,
} from '../../../helpers/options';
import { RELATIONS_KEYS } from '../../../helpers/tableKeys';
import { codeList } from '../../../utils/filters';
import saveFile from '../../../utils/saveFile';
import { createErrorNotification } from '../../../utils/createErrorNotification';
import PATH from '../../../helpers/routes.json';
import { useAuth } from '../../../context/auth/AuthContext';

const {
  CODE,
  PROFILE,
  RELATED_CODE,
  CODE_NAME,
  RELATION_TYPE,
  LANGUAGE_ID,
  RELATED_CODE_NAME,
  PROFILES,
  NAME,
  COEFFICIENT,
  CODING,
  RELATED_CODING,
  COMMENT,
} = RELATIONS_KEYS;
const { OFFSET, LIMIT } = codeList;

const initialFilter = {
  language: null,
  additions: [NAME, PROFILE],
};

const ComplexCodeView = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [codeData, setCodeData] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [languageList, setLanguageList] = useState(null);
  const [hasMoreData, setHasMoreData] = useState(true);

  const [filter, setFilter] = useState(initialFilter);
  const [isLoading, setIsLoading] = useState(true);
  const [isOptionsLoading, setIsOptionsLoading] = useState(true);
  const [isSearchParamsLoading, setIsSearchParamsLoading] = useState(true);
  const [fetchKey, setFetchKey] = useState(0);

  const {
    isDeleting,
    itemToDelete: linkToDelete,
    openDeleteDialog,
    closeDeleteDialog,
    confirmDelete,
  } = useDeleteRow({
    onDelete: item => removeLink(item.code_relation_id),
    onSuccess: () => setFetchKey(prev => prev + 1),
  });

  const selectedProfiles = searchParams.get('profiles');

  const location = useLocation();
  const navigate = useNavigate();

  const { user } = useAuth();

  const { t } = useTranslation(['relations', 'promptbar', 'common']);

  const title = t('common:pages.dictionaries.tabs.complex_code');
  const pageTitle = t('promptbar:pageTitle', {
    title,
  });

  useTitle(pageTitle);

  usePersistColumns('complexColumns', ['language', 'additions', 'profiles']);

  useEffect(() => {
    if (isOptionsLoading || !user) {
      return;
    }

    const savedLanguage = searchParams.get('language');
    if (
      savedLanguage &&
      languageList.some(language => language.name === savedLanguage)
    ) {
      setFilter(prev => ({ ...prev, language: savedLanguage }));
    }

    if (
      searchParams.has('additions') &&
      !searchParams.get('additions')?.length
    ) {
      setFilter(prev => ({ ...prev, additions: [] }));
    } else if (
      searchParams.has('additions') &&
      searchParams.get('additions')?.length
    ) {
      const additions = searchParams.get('additions').split(',');
      setFilter(prev => ({ ...prev, additions }));
    } else {
      searchParams.set('additions', initialFilter.additions.join(','));
      setFilter(prev => ({ ...prev, additions: initialFilter.additions }));
    }

    if (
      !searchParams.has('profiles') ||
      !searchParams.get('profiles')?.length
    ) {
      const defaultProfiles = user.profiles
        .filter(pr => pr.is_default)
        .map(el => el.profile_name);
      if (defaultProfiles.length) {
        searchParams.set(PROFILES, defaultProfiles.join(','));
      }
    }

    setSearchParams(searchParams, { replace: true });
    setIsSearchParamsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOptionsLoading]);

  useEffect(() => {
    if (!user) {
      return;
    }

    const fetchOptions = async () => {
      try {
        const [langs] = await Promise.all([getLanguages()]);
        const defaultLang =
          langs.data.find(el => el.id === user.data_language_id)?.name ||
          languageOption[0];
        const savedLanguage = new URLSearchParams(window.location.search).get(
          'language',
        );
        const selectedLanguage = langs.data.some(
          lang => lang.name === savedLanguage,
        )
          ? savedLanguage
          : defaultLang;

        setProfiles(user.profiles);
        setLanguageList(langs.data);
        setFilter(prev => ({ ...prev, language: selectedLanguage }));
      } catch (error) {
        console.log(error.message);
      } finally {
        setIsOptionsLoading(false);
      }
    };
    fetchOptions();
    // searchParams is read only when the language options are initialized.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchData = useCallback(
    async ({ params, signal }) => {
      if (!languageList || !profiles) {
        return;
      }

      setIsLoading(true);

      const currentLanguageID = languageList.find(
        lang => lang.name === params.globalFilter,
      ).id;

      const newParams = {
        [OFFSET]: params[OFFSET],
        [LIMIT]: params[LIMIT],
        [LANGUAGE_ID]: currentLanguageID,
        [RELATION_TYPE]: RELATIONS.COMPLEX,
        ...(params[CODE]
          ? {
              code: params[CODE].replace(/[;, ]+/g, ';').replace(/[;\s]*$/, ''),
            }
          : {}),
        ...(params[CODE_NAME]
          ? {
              code_name: params[CODE_NAME],
            }
          : {}),
        ...(params[RELATED_CODE]
          ? {
              related_code: params[RELATED_CODE].replace(
                /[;, ]+/g,
                ';',
              ).replace(/[;\s]*$/, ''),
            }
          : {}),
        ...(params[RELATED_CODE_NAME]
          ? {
              related_code_name: params[RELATED_CODE_NAME],
            }
          : {}),
        ...(params[PROFILES] && params[PROFILES]?.length > 0
          ? Array.isArray(params[PROFILES])
            ? { profile_name: params[PROFILES] }
            : { profile_name: params[PROFILES].split(',') }
          : {}),
        ...(params[COEFFICIENT]
          ? {
              coefficient: params[COEFFICIENT],
            }
          : {}),
        ...(params[CODING]
          ? {
              coding: params[CODING],
            }
          : {}),
        ...(params[RELATED_CODING]
          ? {
              related_coding: params[RELATED_CODING],
            }
          : {}),
        ...(params[COMMENT]
          ? {
              comment: params[COMMENT],
            }
          : {}),
      };
      try {
        const data = await getRelatedCode(newParams, signal);

        if (data) {
          const newData = data.data.map(el => ({
            ...el,
            uniqId: el.code_relation_id,
          }));
          setCodeData(prev =>
            params[OFFSET] === 0 ? [...newData] : [...prev, ...newData],
          );
          setHasMoreData(prev => (params[OFFSET] === 0 ? true : prev));
          if (newData.length < params[LIMIT]) {
            setHasMoreData(false);
          }
          setIsLoading(false);
        }
      } catch (error) {
        createErrorNotification();
        console.log(error.message);
        setIsLoading(false);
      }
    },
    [languageList, profiles],
  );

  // фильтр
  const handleFilterSelect = event => {
    const { value, name } = event.target;
    setFilter(prev => ({ ...prev, [name]: value }));

    searchParams.set(name, value);
    setSearchParams(searchParams);
  };

  const handleFilterReset = () => {
    navigate(location.pathname, { replace: true });
    setTimeout(() => navigate(0));
  };

  const editHandler = rowId => {
    setCodeData(prev =>
      prev.map(row =>
        row.uniqId === rowId ? { ...row, isEdit: true, prevState: row } : row,
      ),
    );
  };

  const handleChangeRowData = useCallback(
    ({ index, value, columnName, isValid }) => {
      setCodeData(prev =>
        prev.map(row =>
          row.uniqId === index
            ? {
                ...row,
                [columnName]: value,
                isValid: { ...row.isValid, [columnName]: isValid },
              }
            : row,
        ),
      );
    },
    [],
  );

  const applyChangesHandler = async (rowId, data) => {
    if (data.isValid && !Object.values(data.isValid).every(Boolean)) {
      return;
    }

    const isCoefficientChanged =
      data.prevState?.[COEFFICIENT] !== data[COEFFICIENT];
    const isCommentChanged = data.prevState?.[COMMENT] !== data[COMMENT];

    if (!isCoefficientChanged && !isCommentChanged) {
      setCodeData(prev =>
        prev.map(row =>
          row.uniqId === rowId
            ? {
                ...row,
                isEdit: false,
                prevState: undefined,
                isValid: undefined,
              }
            : row,
        ),
      );
      return;
    }

    try {
      setIsLoading(true);
      const currentLanguageId = languageList.find(
        lang => lang.name === filter.language,
      )?.id;

      await updateLink(data.code_relation_id, currentLanguageId, {
        coefficient: Number(data[COEFFICIENT]),
        comment: data[COMMENT] || '',
      });
      setFetchKey(prev => prev + 1);
      setCodeData(prev =>
        prev.map(row =>
          row.uniqId === rowId
            ? {
                ...row,
                isEdit: false,
                prevState: undefined,
                isValid: undefined,
              }
            : row,
        ),
      );
    } catch (error) {
      createErrorNotification();
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const cancelChangesHandler = rowId => {
    setCodeData(prev =>
      prev.map(row => (row.uniqId === rowId ? { ...row.prevState } : row)),
    );
  };

  // удалить связь
  const deleteLink = data => {
    if (data.isEdit || isDeleting) {
      return null;
    }

    return <DeleteRowButton onClick={() => openDeleteDialog(data)} />;
  };

  const handleDownloadExcel = async () => {
    try {
      setIsLoading(true);

      const currentLang = languageList.find(
        lang => filter.language === lang.name,
      );
      const profiles = searchParams.get('profiles')?.split(',');
      const params = {
        [LANGUAGE_ID]: currentLang?.id || languageOption[1].id,
        [RELATION_TYPE]: RELATIONS.COMPLEX,
        profile_name: profiles,
      };

      const result = await downloadRelatedCode(params);

      if (result) {
        const blob = new Blob([result.data], {
          type: 'application/octet-stream',
        });
        saveFile(blob, 'complex.xlsx');
      }
    } catch (error) {
      createErrorNotification();
      console.log(error);
    } finally {
      setIsLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      {
        Header: <NowrapWrapper title={t(`table.code`)} />,
        accessor: CODE,
        id: CODE,
        style: { width: '150px', minWidth: '150px', maxWidth: '150px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.coding`)} />,
        accessor: CODING,
        id: CODING,
        show: filter.additions.includes(CODING),
        style: { width: '150px', minWidth: '150px', maxWidth: '150px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.code_name`)} />,
        accessor: CODE_NAME,
        id: CODE_NAME,
        show: filter.additions.includes(NAME),
        style: { width: '50%' },
      },
      {
        Header: <NowrapWrapper title={t(`table.complex_code`)} />,
        accessor: RELATED_CODE,
        id: RELATED_CODE,
        style: { width: '150px', minWidth: '150px', maxWidth: '150px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.complex_coding`)} size={12} />,
        accessor: RELATED_CODING,
        id: RELATED_CODING,
        show: filter.additions.includes(CODING),
        style: { width: '170px', minWidth: '170px', maxWidth: '170px' },
      },
      {
        Header: <NowrapWrapper title={t(`table.complex_code_name`)} />,
        accessor: RELATED_CODE_NAME,
        id: RELATED_CODE_NAME,
        show: filter.additions.includes(NAME),
        style: { width: '50%' },
      },
      {
        Header: <NowrapWrapper title={t(`table.coefficient`)} />,
        accessor: COEFFICIENT,
        id: COEFFICIENT,
        filterInputType: 'number',
        show: filter.additions.includes(COEFFICIENT),
        style: { width: '120px', minWidth: '120px', maxWidth: '120px' },
        Cell: ({ value, ...cell }) =>
          cell.row.original.isEdit ? (
            <EditableCell
              value={value}
              cell={cell}
              onChangeData={handleChangeRowData}
              pathname={location.pathname}
            />
          ) : (
            value
          ),
      },
      {
        Header: <NowrapWrapper title={t(`table.comment`)} />,
        accessor: COMMENT,
        id: COMMENT,
        show: filter.additions.includes(COMMENT),
        style: { width: '130px', minWidth: '130px', maxWidth: '130px' },
        Cell: ({ value, ...cell }) =>
          cell.row.original.isEdit ? (
            <EditableCell
              value={value}
              cell={cell}
              onChangeData={handleChangeRowData}
              pathname={location.pathname}
            />
          ) : (
            value
          ),
      },
      {
        Header: <NowrapWrapper title={t(`table.profile`)} />,
        id: PROFILES,
        accessor: PROFILES,
        show: filter.additions.includes(PROFILE),
        style: { width: '9%', maxWidth: '200px', minWidth: '200px' },
        Filter: cell => {
          const profilesNames = profiles.map(el => el.profile_name);
          return (
            <SelectColumnFilter
              cell={cell}
              options={profilesNames}
              multiple
              isValueRequired
            />
          );
        },

        Cell: ({ value }) => value.map(item => item.name).join('; '),
      },
    ],
    [filter.additions, handleChangeRowData, location.pathname, profiles, t],
  );

  if (isOptionsLoading) {
    return <OverLayLoader />;
  }

  return (
    <>
      {(isLoading || isSearchParamsLoading) && <OverLayLoader />}
      <Box>
        <DictionaryFilter
          multipleLanguage={false}
          filter={filter}
          onFilterReset={handleFilterReset}
          options={optionalRelatedCodeColumns}
          onFilterSelect={handleFilterSelect}
          onDownload={handleDownloadExcel}
        />

        {!isSearchParamsLoading && (
          <FunctionalTable
            key={fetchKey}
            type="sticky"
            columns={columns}
            data={codeData}
            fetchData={fetchData}
            hasMoreData={hasMoreData}
            isInfiniteScroll
            pageSize={50}
            globalFilter={filter.language}
            onEdit={editHandler}
            applyChanges={applyChangesHandler}
            cancelChanges={cancelChangesHandler}
            actionColumnComponent={deleteLink}
          />
        )}

        <FloatingActionButtonHolder>
          <Tooltip title={t('promptbar:addLink')} arrow>
            <Link
              to={`${PATH.dictionaries.addComplexLink}/${
                filter.language
              }?profiles=${encodeURIComponent(selectedProfiles ?? '')}`}
            >
              <Fab color="primary" aria-label="add code">
                <AddIcon />
              </Fab>
            </Link>
          </Tooltip>
        </FloatingActionButtonHolder>
      </Box>

      <DeleteConfirmDialog
        open={Boolean(linkToDelete)}
        onClose={closeDeleteDialog}
        onConfirm={confirmDelete}
        isDeleting={isDeleting}
        title={t('relations:deleteDialog.title')}
        description={t('relations:deleteDialog.description', {
          code: linkToDelete?.[CODE] || '-',
          relatedCode: linkToDelete?.[RELATED_CODE] || '-',
        })}
      />
    </>
  );
};

export default ComplexCodeView;
